export const RouteColors = {
  Level0: '',
  Level1: '#45F010',
  Level2: '#009F10',
  Level3: '#FDD800',
  Level4: '#EDB000',
  Level5: '#3AA0FF',
  Level6: '#7570B3',
  Level7: '#666666',
} as const;

export const RouteTextColors: Record<string, string> = {
  [RouteColors.Level0]: '',
  [RouteColors.Level1]: 'var(--dv-base-black)',
  [RouteColors.Level2]: 'var(--dv-base-white)',
  [RouteColors.Level3]: 'var(--dv-base-black)',
  [RouteColors.Level4]: 'var(--dv-base-white)',
  [RouteColors.Level5]: 'var(--dv-base-white)',
  [RouteColors.Level6]: 'var(--dv-base-white)',
  [RouteColors.Level7]: 'var(--dv-base-white)s',
};

export const RouteColorIcons = {
  Level0: 'assets/route-network-icons/level_0.svg',
  Level1: 'assets/route-network-icons/level_1.svg',
  Level2: 'assets/route-network-icons/level_2.svg',
  Level3: 'assets/route-network-icons/level_3.svg',
  Level4: 'assets/route-network-icons/level_4.svg',
  Level5: 'assets/route-network-icons/level_5.svg',
  Level6: 'assets/route-network-icons/level_6.svg',
  Level7: 'assets/route-network-icons/level_7.svg',
};

export type RouteColorKey = keyof typeof RouteColors;

export enum RouteNetworkMaintainerType {
  TrailGroomer = 1,
  Snowmobile = 2,
  ATV = 3,
}

export const RouteNetworkMaintainerIconName: { [key: number]: string } = {
  [RouteNetworkMaintainerType.TrailGroomer]: 'maintainer',
  [RouteNetworkMaintainerType.Snowmobile]: 'snowmobile',
  [RouteNetworkMaintainerType.ATV]: 'ATV',
};

export enum RouteSegmentType {
  Ski = 1,
  Walking = 2,
}

/** feature (or type) names shown as chips in the route sheet, in this order */
export const RouteSheetChipNames = ['Lit', 'Classic', 'Skating'];

/** map pane for white route edges, placed right below overlayPane (z-index 400) so every route line is drawn above them */
export const RouteEdgePane = 'routeEdgePane';

/** map pane for walking segment markers, between route lines (overlayPane, 400) and all other markers (markerPane, 600) */
export const WalkingSegmentMarkerPane = 'walkingSegmentMarkerPane';

/** all halos, then casings, then lines, so no halo covers a neighbouring line */
export const RouteHighlightPanes = {
  halo: 'routeHighlightHaloPane',
  casing: 'routeHighlightCasingPane',
  line: 'routeHighlightLinePane',
} as const;

/** route sheet size, as in sidesheet.component.scss */
export const RouteSheetSize = {
  width: 448,
  narrowWidth: 400,
  narrowMaxWidth: 1100,
  phoneMaxWidth: 768,
  phoneHeightRatio: 0.5,
} as const;

/** walking trail footprint icon, viewBox 0 0 12 13 */
export const FootprintIconPath =
  'M2.625 0C3.45139 0 4.09548 0.367016 4.55729 1.10104C5.01912 1.83507 5.25 2.63472 5.25 3.5C5.25 3.89861 5.2087 4.27778 5.12604 4.6375C5.04338 4.99722 4.94859 5.31808 4.84167 5.6L0.685417 6.43125C0.539583 6.14932 0.388891 5.75797 0.233333 5.25729C0.0777759 4.7566 0 4.17083 0 3.5C0 2.49861 0.247917 1.66493 0.74375 0.998958C1.23958 0.332984 1.86667 0 2.625 0ZM3.42708 9.91667C2.73681 9.91667 2.19965 9.67604 1.81563 9.19479C1.4316 8.71354 1.21528 8.15692 1.16667 7.525L4.92917 6.76667C5.00693 6.93192 5.07984 7.11912 5.14792 7.32812C5.21599 7.53713 5.25 7.75349 5.25 7.97708C5.25 8.53125 5.07745 8.99307 4.73229 9.3625C4.38715 9.73192 3.95208 9.91667 3.42708 9.91667ZM9.04167 2.91667C9.8 2.91667 10.4271 3.24965 10.9229 3.91562C11.4188 4.5816 11.6667 5.41526 11.6667 6.41667C11.6667 7.0875 11.5889 7.67328 11.4333 8.17396C11.2778 8.67463 11.1271 9.06599 10.9813 9.34792L6.825 8.51667C6.71808 8.23474 6.62328 7.91391 6.54063 7.55417C6.45797 7.19442 6.41667 6.81526 6.41667 6.41667C6.41667 5.55141 6.64755 4.75173 7.10938 4.01771C7.5712 3.28368 8.21526 2.91667 9.04167 2.91667ZM8.23958 12.8333C7.71458 12.8333 7.27953 12.6486 6.93438 12.2792C6.58922 11.9097 6.41667 11.4479 6.41667 10.8937C6.41667 10.6702 6.45068 10.4538 6.51875 10.2448C6.58683 10.0358 6.65974 9.84859 6.7375 9.68333L10.4854 10.4417C10.4368 11.0736 10.2229 11.6302 9.84375 12.1115C9.46458 12.5927 8.92984 12.8333 8.23958 12.8333Z';

export const MapTileLayer = {
  Aerial: 'aerial',
  Vector: 'vector',
};

export type MapTileLayerType = (typeof MapTileLayer)[keyof typeof MapTileLayer];

export const enum PoiIconKey {
  IceCrack = 'icon-ice-crack',
  WaterOnIce = 'icon-water-on-ice',
  LakeIce = 'icon-lake-ice',
  SlopeUp = 'icon-slope-up',
  SlopeDown = 'icon-slope-down',
  SlopeLong = 'icon-slope-long',
  SlopeTurn = 'icon-slope-turn',
  Slippery = 'icon-slippery',
  RoadCrossing = 'icon-road-crossing',
  Road80 = 'icon-road-80',
  RoadGravel = 'icon-road-gravel',
  QuestionTrail = 'icon-question-trail',
  Rock = 'icon-rock',
  SoftSnow = 'icon-soft-snow',
  SnowCatWarning = 'icon-snowcat-warning',
  TrailJunction = 'icon-trail-junction',
  Cabin = 'icon-cabin',
  Shop = 'icon-shop',
  Parking = 'icon-parking',
  TurnRight = 'icon-turn-right',
  TurnLeft = 'icon-turn-left',
  Cafe = 'icon-cafe',
  BusStop = 'icon-bus-stop',
  Camp = 'icon-camp',
  Cancel = 'icon-cancel',
}

export const PoiIconMapping = {
  [PoiIconKey.IceCrack]: 'ice_crack_sign',
  [PoiIconKey.WaterOnIce]: 'water_on_ice',
  [PoiIconKey.LakeIce]: 'ice_crack',
  [PoiIconKey.SlopeUp]: 'slope_up_sign',
  [PoiIconKey.SlopeDown]: 'slope_sign',
  [PoiIconKey.SlopeLong]: 'slope_sign',
  [PoiIconKey.SlopeTurn]: 'turn_sign',
  [PoiIconKey.Slippery]: 'slippery_sign',
  [PoiIconKey.RoadCrossing]: 'road_sign',
  [PoiIconKey.Road80]: 'road_80',
  [PoiIconKey.RoadGravel]: 'road_gravel_sign',
  [PoiIconKey.QuestionTrail]: 'invisible_path',
  [PoiIconKey.Rock]: 'rock_sign',
  [PoiIconKey.SoftSnow]: 'snow_soft_sign',
  [PoiIconKey.SnowCatWarning]: 'snow_groomer',
  [PoiIconKey.TrailJunction]: 'arrow_junction_sign',
  [PoiIconKey.Cabin]: 'cabin_sign',
  [PoiIconKey.Shop]: 'shopping_cart_sign',
  [PoiIconKey.Parking]: 'parking_sign',
  [PoiIconKey.TurnRight]: 'arrow_right_sign',
  [PoiIconKey.TurnLeft]: 'arrow_left_sign',
  [PoiIconKey.Cafe]: 'cafe_sign',
  [PoiIconKey.BusStop]: 'bus_sign',
  [PoiIconKey.Camp]: 'shelter_sign',
  [PoiIconKey.Cancel]: 'invisible_path',
} as const satisfies Record<PoiIconKey, string>;
